import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';
import Header from './components/Header';
import Footer from './components/Footer';
import WhatsAppFloat from './components/WhatsAppFloat';
import ScrollToTop from './components/ScrollToTop';
import ScrollProgressBar from './components/ScrollProgressBar';
import HomePage from './pages/HomePage';
import SobrePage from './pages/SobrePage';
import EmpresasPage from './pages/EmpresasPage';
import CandidatosPage from './pages/CandidatosPage';
import ContatoPage from './pages/ContatoPage';
import VagaDetailPage from './pages/VagaDetailPage';
import { CandidatoLoginPage, CandidatoRegisterPage, AdminLoginPage, EmpresaLoginPage, ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';

import GoogleCallback from './pages/GoogleCallback';
import AcessarPage from './pages/AcessarPage';
import ConteudosPage from './pages/ConteudosPage';
import ArtigoPage from './pages/ArtigoPage';
import LocalSeoPage from './pages/LocalSeoPage';
import ServiceSeoPage from './pages/ServiceSeoPage';
import { LOCAL_SEO_SLUGS } from './data/localSeo';
import { SERVICE_SEO_SLUGS } from './data/serviceSeo';
import { SEGMENT_SEO_SLUGS } from './data/segmentSeo';

// Painéis carregados sob demanda: o site público não baixa o código dos portais.
const CandidatoDashboard = lazy(() => import('./pages/CandidatoDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ComercialDashboard = lazy(() => import('./pages/ComercialDashboard'));
const EmpresaDashboard = lazy(() => import('./pages/EmpresaDashboard'));

const PortalFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="portal-loading">
    <div className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-hm-orange animate-spin" />
  </div>
);

const SITE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const Layout = ({ children }) => {
  const location = useLocation();
  const isPortal = location.pathname.startsWith('/portal') || location.pathname.startsWith('/admin') || location.pathname === '/acessar';
  if (isPortal) return <>{children}</>;
  const canonical = `${SITE}${location.pathname === '/' ? '/' : location.pathname.replace(/\/$/, '')}`;
  return (
    <>
      <Helmet>
        <link rel="canonical" href={canonical} />
      </Helmet>
      <Header />
      {children}
      <Footer />
      <WhatsAppFloat />
    </>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  // Processa o retorno do Google Auth ANTES das rotas normais (evita race condition)
  if (location.hash && location.hash.includes('session_id=')) {
    return <GoogleCallback />;
  }
  return (
    <Layout>
      <Suspense fallback={<PortalFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/acessar" element={<AcessarPage />} />
        <Route path="/sobre-nos" element={<SobrePage />} />
        <Route path="/para-empresas" element={<EmpresasPage />} />
        <Route path="/para-candidatos" element={<CandidatosPage />} />
        <Route path="/contato" element={<ContatoPage />} />
        <Route path="/conteudos" element={<ConteudosPage />} />
        <Route path="/conteudos/:slug" element={<ArtigoPage />} />
        {LOCAL_SEO_SLUGS.map((s) => (
          <Route key={s} path={`/${s}`} element={<LocalSeoPage />} />
        ))}
        {SERVICE_SEO_SLUGS.map((s) => (
          <Route key={s} path={`/${s}`} element={<ServiceSeoPage />} />
        ))}
        {SEGMENT_SEO_SLUGS.map((s) => (
          <Route key={s} path={`/${s}`} element={<ServiceSeoPage />} />
        ))}
        <Route path="/servicos" element={<Navigate to="/para-empresas" replace />} />
        <Route path="/vaga/:id" element={<VagaDetailPage />} />
        <Route path="/portal/candidato/login" element={<CandidatoLoginPage />} />
        <Route path="/portal/candidato/cadastro" element={<CandidatoRegisterPage />} />
        <Route path="/recuperar-senha" element={<ForgotPasswordPage />} />
        <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
        <Route path="/portal/candidato" element={<CandidatoDashboard />} />
        <Route path="/portal/admin/login" element={<AdminLoginPage />} />
        <Route path="/portal/admin" element={<AdminDashboard />} />
        <Route path="/portal/comercial" element={<ComercialDashboard />} />
        <Route path="/portal/empresa/login" element={<EmpresaLoginPage />} />
        <Route path="/portal/empresa" element={<EmpresaDashboard />} />
      </Routes>
      </Suspense>
    </Layout>
  );
};

function App() {
  // Remove "Made with Emergent" badge injected by the platform proxy
  React.useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
    };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <ScrollProgressBar />
            <AppRoutes />
            <Toaster />
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
